// Constants
import {
  CHATBOT_SIDEBAR_CLASSES,
  CHATBOT_MESSAGES,
  InvalidItemSection,
  ITEM_SECTION_PATTERNS,
  FAILURE_CONFIGS,
  LIST_TRUNCATE_LIMIT,
  EDUCATION_ERROR_TEMPLATES,
  CERTIFICATION_ERROR_TEMPLATES,
  DEFAULT_CARD_FIELDS,
  CARD_BACKGROUNDS,
} from '@/constants/chatbot';
import { SKILL_PATTERN, MESSAGE_PATTERN } from '@/constants/regex';
import { DATE_FORMATS } from '@shared/constants';
import {
  EmployeeRelationshipRole,
  EmployeeTrainingStatus,
} from '@/constants/employee';
import { ActionType } from '@/constants/action';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorage';
import { COPILOT_TOOLS } from '@/constants/tools';

// Utils
import {
  convertToPNG,
  convertToPNGBase64,
  convertUrlToFile,
  resizeImageToMax,
  formatPhoneNumber,
  formatDateStringToMonthYear,
  formatDate,
  formatDateStringToTimezone,
  toPlural,
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@shared/utils';
import {
  mapMentorAssignmentData,
  mapMentorUnassignmentData,
  mapMenteeAssignmentData,
  mapMenteeUnassignmentData,
} from '@/utils/employeeRelationship';
import { getUsernameFromEmail } from '@/utils/common';
import {
  normalizeString,
  formatTruncatedList,
  formatListWithAnd,
} from '@/utils/textUtils';
import {
  isEducationInError,
  extractSchoolNames,
  buildEducationSuccessMessage,
  matchEducationByKeyFields,
} from '@/utils/education';
import {
  isCertificationInError,
  extractCertificationNames,
  buildCertificationSuccessMessage,
} from '@/utils/certification';

// Types
import { ActivityType, Skill, ProjectStack } from '@shared/types';
import { ArgsType } from '@/types/chat';
import {
  EmployeeInfo,
  EmployeeEducation,
  EmployeeCertification,
} from '@/types/employee';
import {
  TrainingCardData,
  PresentationCardData,
  FeedbackCardData,
  ParticipantCardData,
  ParticipantRecipient,
} from '@/types/training';
import type {
  AssignMentorResponse,
  AssignMenteeResponse,
  UnassignMentorResponse,
  UnassignMenteeResponse,
  TrainingPresentationResponse,
  TrainingFeedbackResponse,
  EmployeeEducationResponse,
} from '@/types/api';
import type { RelationshipCardData } from '@/types/employeeRelationship';
import type {
  TrainingArchiveTopicMessageOptions,
  TrainingArchivePresentationMessageOptions,
} from '@/types/training';
import {
  InvalidItemsData,
  EmployeeSkillsUpdateResult,
  ProjectTechStackUpdateResult,
  EducationUpdateResult,
  CertificationUpdateResult,
  EducationFailure,
  CertificationFailure,
  UpdateOperationResult,
  MessageType,
  FailureReason,
  ParsedError,
  FailureConfig,
  TrainingCardFieldsConfig,
  PickedTrainingCardData,
  PickTrainingCardFieldsParams,
  PresentationArgs,
  ErrorTemplate,
  UpdateProcessConfig,
  OperationValidation,
  ValidationState,
  ItemCollection,
  ItemCollectionUpdateResult,
} from '@/types/chatbot';
import { Message, MessageRole, Role } from '@copilotkit/runtime-client-gql';
import React from 'react';

/**
 * Gets the layout-workspace element from the DOM
 * @returns The layout element or null if not found
 */
export const getLayoutElement = (): Element | null => {
  if (typeof window === 'undefined') return null;

  return document.querySelector('.layout-workspace');
};

/**
 * Applies sidebar CSS classes to the layout element
 * @param isOpen - Whether the sidebar should be open
 * @param isDesktop - Whether we're on desktop
 */
export const applySidebarClasses = (
  isOpen: boolean,
  isDesktop: boolean,
): void => {
  const { OPEN, CLOSE } = CHATBOT_SIDEBAR_CLASSES;
  const layoutElement = getLayoutElement();

  if (!layoutElement) return;

  layoutElement.classList.remove(OPEN, CLOSE);

  if (!isDesktop) return;

  layoutElement.classList.add(isOpen ? OPEN : CLOSE);
};

/**
 * Removes all sidebar CSS classes from the layout element
 */
export const clearSidebarClasses = (): void => {
  const { OPEN, CLOSE } = CHATBOT_SIDEBAR_CLASSES;
  const layoutElement = getLayoutElement();

  layoutElement?.classList.remove(OPEN, CLOSE);
};

/**
 * Checks if the sidebar is currently open
 * @returns true if sidebar is open, false otherwise
 */
export const isSidebarOpen = (): boolean =>
  getLayoutElement()?.classList.contains(CHATBOT_SIDEBAR_CLASSES.OPEN) ?? false;

/**
 *
 * @param file A uploaded file e.g project logo
 * @param size Small size to show inside chatbot is 55px
 */
export const resizeAndConvertBase64UploadedImage = async (
  file: File,
  size = 55,
) => {
  if (!file) return;

  const processedFile = await convertToPNG(file);
  const resizedFileUrl = await resizeImageToMax(processedFile, size);
  const resizedFile = await convertUrlToFile(
    resizedFileUrl,
    `logo-${new Date().getTime()}.png`,
    'image/png',
  );
  const base64IMG = await convertToPNGBase64(resizedFile);
  return base64IMG;
};

export const mapEmployeeInfoKeyToField = (key: string): string | null => {
  const mapping: Record<string, string> = {
    name: 'name',
    gender: 'gender',
    job_title: 'title',
    birthday: 'birthday',
    phone: 'phone',
    address: 'address',
    bio: 'bio',
    joined_date: 'onboarding date',
    status: 'status',
  };

  return mapping[key.toLowerCase()] || null;
};

export const mapFieldToDisplayLabel = (field: string): string => {
  const mapping: Record<string, string> = {
    name: 'Name',
    title: 'Job Title',
    gender: 'Gender',
    birthday: 'Birthday',
    phone: 'Phone',
    address: 'Address',
    bio: 'Bio',
    joined_date: 'Onboarding Date',
  };

  return mapping[field] || field;
};

/**
 * Gets the updated field value from args or result
 * @param field - The field name to get the value for
 * @param args - The args object containing field and value
 * @param result - The result object containing the updated values
 * @returns The string value of the field or empty string
 */
export const getUpdatedFieldValue = (
  field: string,
  args: ArgsType,
  result: ArgsType,
): string => {
  // Return direct value if args matches the current field
  if (args?.field?.toLowerCase() === field.toLowerCase() && args.value) {
    return args.value;
  }

  const argsValue = args?.[field as keyof ArgsType];
  const resultValue = result?.[field as keyof ArgsType];
  const value = argsValue || resultValue;

  return typeof value === 'string' ? value : '';
};

/**
 * Gets the formatted field value from employee info for display
 * @param field - The field name to get the value for
 * @param employeeInfo - The employee info object
 * @returns The formatted string value of the field
 */
export const getEmployeeFieldValue = (
  field: string,
  employeeInfo: EmployeeInfo,
): string => {
  const value = employeeInfo[field as keyof EmployeeInfo];
  const normalizedValue = value?.toString() || '';

  if (field === 'phone') return formatPhoneNumber(normalizedValue);
  if (field === 'birthday' && normalizedValue) {
    return formatDateStringToMonthYear(formatDate(normalizedValue));
  }

  return Array.isArray(value) ? value.join(', ') : normalizedValue;
};

export const normalizeRelationshipResult = (
  result: ArgsType['data'],
): RelationshipCardData | null => {
  if (!result || typeof result !== 'object' || !('data' in result)) {
    return null;
  }

  const { data } = result;

  // Assign mentor: data contains mentor details
  if ('mentor' in data)
    return mapMentorAssignmentData(data as AssignMentorResponse['data']);

  // Unassign mentor: data contains unAssignedMentor
  if ('unAssignedMentor' in data)
    return mapMentorUnassignmentData(data as UnassignMentorResponse['data']);

  // Assign mentee(s): data contains mentees array
  if ('mentees' in data)
    return mapMenteeAssignmentData(data as AssignMenteeResponse['data']);

  // Unassign mentee(s): data contains unAssignedMentees array
  if ('unAssignedMentees' in data)
    return mapMenteeUnassignmentData(data as UnassignMenteeResponse['data']);

  return null;
};

/**
 * Extract education data from result
 */
export const normalizeEducationResult = (
  result: ArgsType['data'],
): EmployeeEducation[] | undefined => {
  if (!result || typeof result !== 'object') return undefined;

  // New format: result.data.education
  if ('data' in result && result.data && typeof result.data === 'object') {
    const data = result.data as { education?: EmployeeEducation[] };
    if (Array.isArray(data.education)) {
      return data.education;
    }
  }

  // Fallback to old format: result.education
  const response = result as EmployeeEducationResponse;
  if ('education' in result && Array.isArray(response.education)) {
    return response.education;
  }

  return undefined;
};

/**
 * Normalizes issue date from string to Date object
 */
const normalizeIssueDate = (
  issue: Date | string | undefined,
): Date | undefined => {
  if (issue instanceof Date) return issue;

  return typeof issue === 'string' && issue ? new Date(issue) : undefined;
};

/**
 * Extract certification data from result
 * Normalizes issue dates from string to Date objects and preserves org field
 */
export const normalizeCertificationResult = (
  result: ArgsType['data'],
): EmployeeCertification[] | undefined => {
  if (!result || typeof result !== 'object') return undefined;

  const data = result as { certs?: EmployeeCertification[] };
  if (!Array.isArray(data.certs)) return undefined;

  return data.certs.map((cert) => ({
    ...cert,
    org: cert.org || undefined,
    issue: normalizeIssueDate(cert.issue),
  }));
};

/**
 * Determines the relationship value for employee context in copilot config
 * @param email - Current user's email
 * @param employeeId - Employee ID from URL (username)
 * @param relationship - Array of relationship roles between current user and employee
 * @returns Object containing currentUser and relationshipValue
 */
export const getEmployeeRelationshipValue = (
  email: string | undefined | '',
  employeeId: string,
  relationship?: EmployeeRelationshipRole[],
) => {
  const currentUser = getUsernameFromEmail(email || '');

  return {
    currentUser,
    relationshipValue:
      currentUser === employeeId ? '' : relationship?.join(',') || '',
  };
};

/**
 * Training action types for chat operations
 */
export enum TrainingAction {
  TRAINING_CREATE = 'TRAINING_CREATE',
  TRAINING_UPDATE = 'TRAINING_UPDATE',
  ASSIGN_SUPPORTERS = 'ASSIGN_SUPPORTERS',
  REVOKE_SUPPORTERS = 'REVOKE_SUPPORTERS',
}

/**
 * Gets supporter label with plural handling based on training action
 * @param action - Training action type
 * @param supporterCount - Number of supporters
 * @returns "Assigned Supporter(s)" or "Unassigned Supporter(s)", empty string otherwise
 */
export const getTrainingSupporterLabel = (
  action?: string,
  supporterCount: number = 0,
): string => {
  const actionVerbMap: Record<string, string> = {
    [TrainingAction.ASSIGN_SUPPORTERS]: 'Assigned',
    [TrainingAction.REVOKE_SUPPORTERS]: 'Unassigned',
  };

  const actionVerb = action ? actionVerbMap[action] : '';
  if (!actionVerb) return '';

  return `${actionVerb} ${toPlural('Supporter', supporterCount)}`;
};

const createCardFields = (
  config: Partial<TrainingCardFieldsConfig>,
): TrainingCardFieldsConfig => ({
  ...DEFAULT_CARD_FIELDS,
  ...config,
});

/**
 * Determines which fields should be displayed in the training info card based on the action and args
 * @param action - The action type (TRAINING_CREATE, TRAINING_UPDATE, ASSIGN_SUPPORTERS, REVOKE_SUPPORTERS, TRAINING_UPDATE_PRESENTATION)
 * @param args - The args object containing the update information
 * @returns Object with flags indicating which fields to show
 */
export const getTrainingCardFields = (
  action?: string,
  args?: {
    desc?: string;
    status?: string;
    newValueTopic?: string;
    estEndDate?: string;
    startDate?: string;
  },
): TrainingCardFieldsConfig => {
  const { desc, status, newValueTopic, estEndDate, startDate } = args || {};
  const isStatusCompleted = status === EmployeeTrainingStatus.COMPLETED;

  const hasValue = {
    description: Boolean(desc),
    estimatedEndDate: Boolean(estEndDate),
    startDate: Boolean(startDate),
  };

  // Update desc only will not show the card
  if (
    hasValue.description &&
    !status &&
    !newValueTopic &&
    !hasValue.estimatedEndDate &&
    !hasValue.startDate
  ) {
    return createCardFields({
      showTopicLabel: false,
      showStatus: false,
      showCard: false,
    });
  }

  const actionHandlers: Record<string, () => TrainingCardFieldsConfig> = {
    // Training creation - card shows topic label, status, start date, and est end date if provided
    [TrainingAction.TRAINING_CREATE]: () =>
      createCardFields({
        showStart: hasValue.startDate,
        showEstEnd: hasValue.estimatedEndDate,
      }),

    // Updating a training item
    // 1. update topic label - only show topicLabel and status
    // 2. update status - only show topicLabel and status
    // 3. update start date - show topicLabel, status and start date
    // 4. update est end date - only show topicLabel, status and est end date
    // 5. update status to completed - show topicLabel, status and actual end date
    [TrainingAction.TRAINING_UPDATE]: () =>
      createCardFields({
        showStart: hasValue.startDate,
        showEstEnd: hasValue.estimatedEndDate,
        showActualEnd: isStatusCompleted,
      }),

    // Assigning or revoking supporters will show the supporter list
    [TrainingAction.ASSIGN_SUPPORTERS]: () =>
      createCardFields({ showSupporters: true }),

    [TrainingAction.REVOKE_SUPPORTERS]: () =>
      createCardFields({ showSupporters: true }),
  };

  // Execute the handler if action is matched
  if (action && actionHandlers[action]) {
    return actionHandlers[action]();
  }

  // Default: show all fields
  return createCardFields({
    showStart: true,
    showEstEnd: true,
    showActualEnd: true,
    showSupporters: true,
  });
};

/**
 * Picks only the fields that should be displayed based on cardFields config
 * @param data - The training data object
 * @param params - Combined card fields configuration and args values
 * @returns Object with only the fields that should be displayed
 */
export const pickTrainingCardFields = (
  data: TrainingCardData,
  params: PickTrainingCardFieldsParams,
): PickedTrainingCardData => ({
  user: data.user,
  ...(params.showTopicLabel && {
    topicLabel:
      params.newValueTopic || params.resultTopicLabel || data.topicLabel,
  }),
  ...(params.showStatus && {
    status: data.status || params.statusFromArgs,
  }),
  ...(params.showStart && {
    start: data.start || params.startDateFromArgs,
  }),
  ...(params.showEstEnd && {
    estEnd: data.estEnd || params.estEndDateFromArgs,
  }),
  ...(params.showActualEnd && { actualEnd: data.actualEnd }),
  ...(params.showSupporters && { supporters: data.supporters }),
});

/**
 * Determines display conditions for training info card and success message
 * @param desc - Description of the training
 * @param training - Training topic name
 * @param newValueTopic - New topic value if topic is being changed
 * @param action - Training action type
 * @param args - Additional args for determining card visibility
 * @returns Object with display condition flags for rendering
 */
export const getTrainingDisplayConditions = (
  desc?: string,
  training?: string,
  newValueTopic?: string,
  action?: string,
  args?: {
    status?: string;
    estEndDate?: string;
    startDate?: string;
  },
): {
  showMessageOnly: boolean;
  showBoth: boolean;
} => {
  const hasDesc = Boolean(desc);
  const isTopicChanged = training !== newValueTopic;
  const cardFields = getTrainingCardFields(action, {
    desc,
    newValueTopic,
    ...args,
  });

  if (!cardFields.showCard) {
    return {
      showMessageOnly: true,
      showBoth: false,
    };
  }

  return {
    showMessageOnly: hasDesc && !isTopicChanged,
    showBoth: !hasDesc || isTopicChanged,
  };
};

/**
 * Formats training feedback message for display
 * Handles both topic feedback and presentation feedback
 * @param data - The result data containing feedback information
 * @param activityType - The activity type to determine if it's presentation feedback
 * @returns Formatted message string:
 *   - Topic feedback: `${relatedName} left feedback for topic "${topicLabel}" at ${occurredAt}`
 *   - Presentation feedback: `${relatedName} left feedback for presentation "${presentationTitle}" of topic "${topicLabel}" at ${occurredAt}`
 * Returns empty string if relatedName or topicLabel is missing
 */
export const formatTrainingFeedbackMessage = (
  data?: {
    data?: {
      relatedName?: string;
      topicLabel?: string;
      presentationTitle?: string;
    };
    occurredAt?: string;
  },
  activityType?: ActivityType,
): string => {
  const { data: feedbackData = {}, occurredAt } = data || {};
  const { relatedName, topicLabel, presentationTitle } = feedbackData;

  if (!relatedName || !topicLabel) return '';

  const formattedDate = occurredAt
    ? formatDateStringToTimezone(occurredAt, DATE_FORMATS.AT_TIME_ON_DATE)
    : '';

  const isPresentationFeedback =
    (activityType === ActivityType.TRAINING_FEEDBACK_PRESENTATION ||
      activityType === ActivityType.TRAINING_UPDATE_FEEDBACK_PRESENTATION) &&
    presentationTitle;

  if (isPresentationFeedback) {
    return `${relatedName} left feedback for presentation "${presentationTitle}" of topic "${topicLabel}" ${formattedDate}`.trim();
  }

  return `${relatedName} left feedback for topic "${topicLabel}" ${formattedDate}`.trim();
};

export const formatTrainingPresentationMessage = (data?: {
  data?: {
    presentationTitle?: string;
    relatedName?: string;
  };
  by?: string;
  topicLabel?: string;
  occurredAt?: string;
}): string => {
  const {
    data: presentationData = {},
    occurredAt,
    topicLabel,
    by,
  } = data || {};
  const { presentationTitle, relatedName } = presentationData;

  if (!presentationTitle || !topicLabel) return '';

  const formattedDate = occurredAt
    ? formatDateStringToTimezone(occurredAt, DATE_FORMATS.AT_TIME_ON_DATE)
    : '';

  // Prefer relatedName over deriving from email
  const displayName = relatedName || getUsernameFromEmail(by || '');

  return `${displayName} recorded presentation ${presentationTitle} for topic "${topicLabel}" ${formattedDate}`.trim();
};

/**
 * Formats training update presentation message for display
 * @param topicLabel - The training topic label
 * @param presentationTitle - The presentation title (optional)
 * @returns Formatted message string
 */
export const formatTrainingUpdatePresentationMessage = (
  topicLabel?: string,
  presentationTitle?: string,
): string => {
  if (!topicLabel) return '';

  if (presentationTitle) {
    return `The information of the presentation titled "${presentationTitle}" for the "${topicLabel}" topic has been updated.`;
  }

  return `The presentation information for the "${topicLabel}" topic has been updated.`;
};

/**
 * Checks if only content was updated (no visible card fields)
 */
export const isContentOnlyUpdate = (
  isUpdatePresentation: boolean,
  args?: PresentationArgs,
): boolean =>
  isUpdatePresentation &&
  Boolean(args?.presentationContent) &&
  !args?.presentation &&
  !args?.newPresentationTitle &&
  !args?.presentationStartDate;

/**
 * Formats training archive/unarchive message (topic or presentation)
 */
const formatTrainingArchiveMessage = (
  options:
    | TrainingArchiveTopicMessageOptions
    | TrainingArchivePresentationMessageOptions,
): string => {
  const { updatedAt, isArchive = true, fallbackMessage } = options;

  const isTopicMessage = 'topicLabel' in options;
  const isPresentationMessage =
    'presentation' in options && 'training' in options;

  if (!isTopicMessage && !isPresentationMessage) return fallbackMessage || '';

  if (isTopicMessage) {
    const { topicLabel } = options as TrainingArchiveTopicMessageOptions;
    if (!topicLabel && !fallbackMessage) return '';
  }

  if (isPresentationMessage) {
    const { presentation, training } =
      options as TrainingArchivePresentationMessageOptions;
    if ((!presentation || !training) && !fallbackMessage) return '';
  }

  const formattedDate = updatedAt
    ? formatDateStringToTimezone(updatedAt, DATE_FORMATS.AT_TIME_ON_DATE)
    : '';

  if (!formattedDate && !fallbackMessage) return '';

  const action = isArchive ? 'archived' : 'unarchived';

  if (isTopicMessage && formattedDate) {
    const { topicLabel } = options as TrainingArchiveTopicMessageOptions;
    const topicName = topicLabel || '';

    return `Training topic ${topicName} has been ${action} successfully ${formattedDate}.`;
  }

  if (isPresentationMessage && formattedDate) {
    const { presentation, training } =
      options as TrainingArchivePresentationMessageOptions;
    if (presentation && training) {
      return `Presentation ${presentation} in the training topic ${training} has been ${action} successfully ${formattedDate}.`;
    }
  }

  return fallbackMessage || '';
};

/**
 * Formats training archive/unarchive topic message
 */
export const formatTrainingArchiveTopicMessage = (
  options: TrainingArchiveTopicMessageOptions,
): string => formatTrainingArchiveMessage(options);

/**
 * Formats training archive/unarchive presentation message
 */
export const formatTrainingArchivePresentationMessage = (
  options: TrainingArchivePresentationMessageOptions,
): string => formatTrainingArchiveMessage(options);

/**
 * Gets display message for training activities
 */
export const getTrainingDisplayMessage = (
  activityType: ActivityType | undefined,
  data: {
    data?: {
      presentationTitle?: string;
      relatedName?: string;
      topicLabel?: string;
    };
    topicLabel?: string;
    occurredAt?: string;
    by?: string;
  },
  resultTopicLabel?: string,
  presentationTitle?: string,
  successMessage?: string,
  updatedAt?: string,
  args?: {
    training?: string;
    presentation?: string;
  },
): string => {
  if (
    activityType === ActivityType.TRAINING_FEEDBACK_RECEIVED ||
    activityType === ActivityType.TRAINING_FEEDBACK_PRESENTATION ||
    activityType === ActivityType.TRAINING_UPDATE_FEEDBACK ||
    activityType === ActivityType.TRAINING_UPDATE_FEEDBACK_PRESENTATION
  ) {
    return formatTrainingFeedbackMessage(data, activityType);
  }

  if (activityType === ActivityType.TRAINING_UPDATE_PRESENTATION) {
    return formatTrainingUpdatePresentationMessage(
      resultTopicLabel,
      presentationTitle,
    );
  }

  if (activityType === ActivityType.TRAINING_PRESENTATION) {
    return formatTrainingPresentationMessage({
      ...data,
      ...(resultTopicLabel && { topicLabel: resultTopicLabel }),
    });
  }

  // Archive/Unarchive topic
  if (
    activityType === ActivityType.TRAINING_ARCHIVE ||
    activityType === ActivityType.TRAINING_UNARCHIVE
  ) {
    return formatTrainingArchiveTopicMessage({
      topicLabel: resultTopicLabel || args?.training,
      updatedAt,
      isArchive: activityType === ActivityType.TRAINING_ARCHIVE,
      fallbackMessage: successMessage,
    });
  }

  // Archive/Unarchive presentation
  if (
    activityType === ActivityType.TRAINING_ARCHIVE_PRESENTATION ||
    activityType === ActivityType.TRAINING_UNARCHIVE_PRESENTATION
  ) {
    return formatTrainingArchivePresentationMessage({
      presentation: args?.presentation || presentationTitle || '',
      training: args?.training || resultTopicLabel || '',
      updatedAt,
      isArchive: activityType === ActivityType.TRAINING_ARCHIVE_PRESENTATION,
      fallbackMessage: successMessage,
    });
  }

  return successMessage || '';
};

/**
 * Extracts and formats presentation card data for display
 * @param topicLabel - The training topic label
 * @param updatedAt - When the presentation was last updated
 * @param resultData - The presentation activity data
 * @param activityType - The activity type from result data
 * @param args - The args object to check which fields were updated
 * @returns PresentationCardData with fields based on activity type
 */
export const getPresentationCardData = (
  topicLabel: string = '',
  updatedAt: string = '',
  resultData: TrainingPresentationResponse['data'],
  activityType?: ActivityType,
  args?: PresentationArgs,
): PresentationCardData => {
  const { data: { presentationTitle = '', presentationAt = '' } = {} } =
    resultData || {};

  // For update presentation, only show fields that were actually updated
  if (activityType === ActivityType.TRAINING_UPDATE_PRESENTATION && args) {
    const hasTitleUpdate =
      Boolean(args.presentation) || Boolean(args.newPresentationTitle);
    const hasDateUpdate = Boolean(args.presentationStartDate);

    return {
      topicLabel,
      ...(hasTitleUpdate && { presentationTitle }),
      ...(hasDateUpdate && { presentationAt }),
      updatedAt,
    };
  }

  return {
    topicLabel,
    presentationTitle,
    presentationAt,
    updatedAt,
  };
};

/**
 * Prepares feedback data for display in FeedbackInfoCard.
 *
 * @param resultData - Feedback activity data containing topic, contributor info, feedbackSentiment, and optionally presentation title
 * @param updatedAt - When the feedback was last updated
 * @param activityType - Type of feedback activity (shows presentation title only for presentation feedback)
 * @returns Formatted data ready to display in FeedbackInfoCard
 */
export const getFeedbackCardData = (
  resultData: TrainingFeedbackResponse['data'],
  updatedAt?: string,
  activityType?: ActivityType,
): FeedbackCardData => {
  const {
    data: {
      topicLabel = '',
      relatedAvatar = '',
      relatedName = '',
      presentationTitle = '',
      feedbackSentiment = '',
    } = {},
  } = resultData || {};

  // Only show presentationTitle for presentation feedback
  const shouldShowPresentationTitle =
    (activityType === ActivityType.TRAINING_FEEDBACK_PRESENTATION ||
      activityType === ActivityType.TRAINING_UPDATE_FEEDBACK_PRESENTATION) &&
    presentationTitle;

  return {
    topicLabel,
    presentationTitle: shouldShowPresentationTitle ? presentationTitle : '',
    feedbackSentiment,
    relatedAvatar,
    relatedName,
    updatedAt: updatedAt || '',
  };
};

/**
 * Gets card background class with fallback to default
 * @param backgroundVariant - Optional custom background variant
 */
export const getCardBackground = (backgroundVariant?: string): string =>
  backgroundVariant || CARD_BACKGROUNDS.DEFAULT;

/**
 * Gets archive card background (amber) or undefined for unarchive
 * @param isArchive - true for archive, false for unarchive
 */
export const getArchiveCardBackground = (
  isArchive: boolean,
): string | undefined => (isArchive ? CARD_BACKGROUNDS.ARCHIVE : undefined);

/**
 * Gets card data for topic archive/unarchive
 */
export const getArchiveTopicCardData = (
  topicLabel: string = '',
  updatedAt: string = '',
): TrainingCardData => ({
  topicLabel,
  user: '',
  updatedAt,
});

/**
 * Gets card data for presentation archive/unarchive
 */
export const getArchivePresentationCardData = (
  topicLabel: string = '',
  presentationTitle: string = '',
  updatedAt: string = '',
): PresentationCardData => ({
  topicLabel,
  presentationTitle,
  updatedAt,
});

/**
 * Gets card data for participant actions (request feedback or revoke participant)
 * @param topicLabel - The training topic label (from result or args)
 * @param presentationTitle - The presentation title (from args)
 * @param updatedAt - When the action was performed
 * @param resultData - The activity data containing recipients
 * @returns ParticipantCardData with recipients list
 */
export const getParticipantCardData = (
  topicLabel: string = '',
  presentationTitle: string = '',
  updatedAt: string = '',
  resultData?: {
    data?: {
      recipients?: Array<{
        avatar?: string;
        email?: string;
        name?: string;
        title?: string;
      }>;
    };
  },
): ParticipantCardData => {
  const recipients: ParticipantRecipient[] =
    resultData?.data?.recipients?.map((recipient) => ({
      avatar: recipient.avatar,
      email: recipient.email,
      name: recipient.name,
      title: recipient.title,
    })) || [];

  return {
    topicLabel,
    presentationTitle,
    recipients,
    updatedAt,
  };
};

/**
 * Converts action type to past tense label for display.
 */
export const getActionLabel = (action: ActionType): string => {
  const labelMap: Record<ActionType, string> = {
    [ActionType.ADD]: 'Added',
    [ActionType.REMOVE]: 'Removed',
    [ActionType.ASSIGN]: 'Assigned',
    [ActionType.UNASSIGN]: 'Unassigned',
    [ActionType.BOTH]: 'Updated',
  };

  return labelMap[action] || action;
};

/** Icon and color for action type. */
export const getActionIconConfig = (
  action: ActionType,
): { icon: string; color: string } =>
  action === ActionType.ADD
    ? { icon: '+', color: 'text-success' }
    : { icon: '-', color: 'text-destructive' };

/**
 * Generates card title based on add/remove action.
 */
export const getCardActionTitle = (
  hasAddItems: boolean,
  hasRemoveItems: boolean,
  entityName: string,
): string => {
  if (hasAddItems && hasRemoveItems) return `Updated ${entityName}`;
  if (hasAddItems) return `Added ${entityName}`;
  if (hasRemoveItems) return `Removed ${entityName}`;

  return '';
};

// Parses comma-separated string into array of trimmed non-empty items
export const parseCommaSeparatedItems = (text: string): string[] =>
  text
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

// Parses error message string into structured invalid items data
const parseInvalidItems = (invalidMessage: string): InvalidItemsData => {
  const result: InvalidItemsData = {
    collectionsNotFound: [],
    itemsNotFound: new Map(),
    itemsAlreadyExisting: new Map(),
  };

  const lines = invalidMessage
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  let currentSection: InvalidItemSection | null = null;

  for (const line of lines) {
    // Detect section header using regex patterns
    if (ITEM_SECTION_PATTERNS.COLLECTIONS_NOT_FOUND.test(line)) {
      currentSection = InvalidItemSection.COLLECTIONS_NOT_FOUND;
      continue;
    }
    if (ITEM_SECTION_PATTERNS.ITEMS_NOT_FOUND.test(line)) {
      currentSection = InvalidItemSection.ITEMS_NOT_FOUND;
      continue;
    }
    if (ITEM_SECTION_PATTERNS.ITEMS_ALREADY_EXISTING.test(line)) {
      currentSection = InvalidItemSection.ITEMS_ALREADY_EXISTING;
      continue;
    }

    // Skip invalid lines (no section context or doesn't start with dash)
    const hasSectionContext = Boolean(currentSection);
    const startsWithDash = line.startsWith('-');
    if (!hasSectionContext || !startsWithDash) continue;

    // Collections not found section uses different format (only collection names)
    if (currentSection === InvalidItemSection.COLLECTIONS_NOT_FOUND) {
      const collectionName = line
        .match(SKILL_PATTERN.SKILL_NAME_ONLY)?.[1]
        ?.trim();
      const isValidCollectionName =
        collectionName && !collectionName.includes(':');
      if (isValidCollectionName) {
        result.collectionsNotFound.push(collectionName);
      }
      continue;
    }

    // Other sections use collection with items format
    const match = line.match(SKILL_PATTERN.SKILL_WITH_ITEMS);
    if (!match) continue;

    const collectionName = match[1]?.trim();
    const itemsString = match[2]?.trim();
    if (!collectionName || !itemsString) continue;

    const items = parseCommaSeparatedItems(itemsString);
    if (items.length === 0 || !currentSection) continue;

    const targetMap = result[currentSection];
    const existing = targetMap.get(collectionName) ?? [];
    targetMap.set(collectionName, [...existing, ...items]);
  }

  return result;
};

// Combines all error messages into single normalized message
const buildNormalizedMessage = (parsed: InvalidItemsData): string => {
  const messages: string[] = [];

  // Build collections not found message
  if (parsed.collectionsNotFound.length) {
    const list = formatListWithAnd(parsed.collectionsNotFound);
    messages.push(`${list} or category doesn't exist`);
  }

  // Build items not found message
  parsed.itemsNotFound.forEach((items, collection) => {
    if (items?.length) {
      const verb = items.length > 1 ? 'are not found' : 'is not found';
      messages.push(`${formatListWithAnd(items)} ${verb} in ${collection}`);
    }
  });

  // Build items already existing message
  parsed.itemsAlreadyExisting.forEach((items, collection) => {
    if (items?.length) {
      const verb = items.length > 1 ? 'already exist' : 'already exists';
      messages.push(`${formatListWithAnd(items)} ${verb} in ${collection}`);
    }
  });

  return formatListWithAnd(messages);
};

// Filters out invalid collections and their items based on exclusion lists
const filterValidItems = <T extends ItemCollection>(
  collections: T[],
  excludedNames: Set<string>,
  excludedItems: Map<string, string[]>,
): T[] => {
  const validCollections: T[] = [];

  for (const collection of collections) {
    const { name, items } = collection;

    if (!name || excludedNames.has(name) || !items) continue;

    const excluded = excludedItems.get(name) ?? [];
    const validItems = items.filter(
      (item): item is string => Boolean(item) && !excluded.includes(item),
    );

    if (validItems.length === 0) continue;

    validCollections.push({ name, items: validItems } as T);
  }

  return validCollections;
};

/**
 * Extracts skill names with item counts, filtering out empty skills.
 */
export const extractSkillsWithCounts = (skills: Skill[]) =>
  skills
    .map((skill) => {
      const { name = '', items = [] } = skill;
      return { name, itemCount: items.length };
    })
    .filter(({ name, itemCount }) => name && itemCount > 0);

/**
 * Generic function to process item collection updates (Skills or ProjectStacks)
 * Handles validation and error messages for any type with { name, items } structure
 */
const processItemCollectionUpdate = <T extends ItemCollection>(
  addItems: T[],
  removeItems: T[],
  invalidMessage: string | undefined,
  defaultSuccessMessage: string,
  entityName: string,
): ItemCollectionUpdateResult<T> => {
  // Early return: no errors, all items are valid
  if (!invalidMessage) {
    const hasSuccessfulOperations =
      addItems.length > 0 || removeItems.length > 0;

    return {
      successfulAdd: addItems,
      successfulRemove: removeItems,
      normalizedMessage: '',
      hasSuccessfulOperations,
      message: { type: MessageType.SUCCESS, text: defaultSuccessMessage },
    };
  }

  const parsed = parseInvalidItems(invalidMessage);
  const collectionsNotFoundSet = new Set(parsed.collectionsNotFound);

  // Filter add items: exclude items that don't exist or already exist
  const successfulAdd = filterValidItems(
    addItems,
    collectionsNotFoundSet,
    parsed.itemsAlreadyExisting,
  );

  // Filter remove items: exclude items that don't exist
  const successfulRemove = filterValidItems(
    removeItems,
    collectionsNotFoundSet,
    parsed.itemsNotFound,
  );

  const normalizedMessage = buildNormalizedMessage(parsed);

  // Check for successful removals not in successfulRemove (cases where all items were removed)
  const hasAdditionalSuccessfulRemovals = removeItems.some((collection) => {
    if (!collection.name || collectionsNotFoundSet.has(collection.name))
      return false;
    if (successfulRemove.some((s) => s.name === collection.name)) return false;

    // Remove all items or check if any items were successfully removed
    if (!collection.items?.length) return true;
    const notFoundItems = parsed.itemsNotFound.get(collection.name) ?? [];

    return collection.items.some((item) => !notFoundItems.includes(item));
  });

  const hasSuccessfulOperations =
    successfulAdd.length > 0 ||
    successfulRemove.length > 0 ||
    hasAdditionalSuccessfulRemovals;

  const hasErrors = normalizedMessage.length > 0;

  // Determine message type and build message text
  const isCompleteFailure = !hasSuccessfulOperations && hasErrors;
  const messageType: MessageType = isCompleteFailure
    ? MessageType.ERROR
    : MessageType.SUCCESS;

  const messageTextMap: Record<MessageType, string> = {
    [MessageType.ERROR]: `Unable to update ${entityName}. ${normalizedMessage}. Please verify and provide valid skill items and categories.`,
    [MessageType.SUCCESS]: normalizedMessage
      ? `${defaultSuccessMessage} However, ${normalizedMessage}.`
      : defaultSuccessMessage,
  };
  const messageText = messageTextMap[messageType];

  return {
    successfulAdd,
    successfulRemove,
    normalizedMessage,
    hasSuccessfulOperations,
    message: { type: messageType, text: messageText },
  };
};

/**
 * Processes skills update operation, handling validation and error messages
 */
export const processSkillsUpdate = (
  addSkills: Skill[],
  removeSkills: Skill[],
  invalidMessage?: string,
  successMessage?: string,
): EmployeeSkillsUpdateResult => {
  const {
    successfulAdd: successfulAddSkills,
    successfulRemove: successfulRemoveSkills,
    ...rest
  } = processItemCollectionUpdate(
    addSkills,
    removeSkills,
    invalidMessage,
    successMessage ?? CHATBOT_MESSAGES.EMPLOYEE_SKILLS_SAVED,
    'skills',
  );

  return {
    successfulAddSkills: successfulAddSkills || [],
    successfulRemoveSkills: successfulRemoveSkills || [],
    ...rest,
  };
};

/**
 * Processes tech stacks update operation, handling validation and error messages
 */
export const processTechStacksUpdate = (
  addStacks: ProjectStack[],
  removeStacks: ProjectStack[],
  invalidMessage?: string,
  successMessage?: string,
): ProjectTechStackUpdateResult => {
  const {
    successfulAdd: successfulAddStacks,
    successfulRemove: successfulRemoveStacks,
    ...rest
  } = processItemCollectionUpdate(
    addStacks,
    removeStacks,
    invalidMessage,
    successMessage ?? CHATBOT_MESSAGES.PROJECT_TECH_STACKS_SAVED,
    'technical stacks',
  );

  return { successfulAddStacks, successfulRemoveStacks, ...rest };
};

const matchFailureReason = (
  message: string | undefined,
  failureConfigs: FailureConfig[],
): ParsedError | null => {
  if (!message?.trim()) return null;
  const normalized = message.toLowerCase();
  const config = failureConfigs.find((c) => normalized.includes(c.pattern));

  return config
    ? { reason: config.reason, operation: config.operation, text: normalized }
    : null;
};

const partitionByErrors = <TItem, TFailure>(
  requested: TItem[],
  errors: ParsedError[],
  operation: ActionType,
  isItemInError: (item: TItem, errorText: string) => boolean,
  createFailure: (item: TItem, reason: FailureReason) => TFailure,
): { succeeded: TItem[]; failed: TFailure[] } => {
  const relevantErrors = errors.filter((e) => e.operation === operation);
  const failures = new Map<TItem, FailureReason>();

  for (const item of requested) {
    const matchingError = relevantErrors.find((err) =>
      isItemInError(item, err.text),
    );
    if (matchingError) failures.set(item, matchingError.reason);
  }

  return {
    failed: Array.from(failures, ([item, reason]) =>
      createFailure(item, reason),
    ),
    succeeded: requested.filter((item) => !failures.has(item)),
  };
};

// Validates add and remove operations separately against errors
const validateOperations = <TItem, TFailure>(
  addRequested: TItem[],
  removeRequested: TItem[],
  errors: ParsedError[],
  isItemInError: (item: TItem, errorText: string) => boolean,
  createFailure: (item: TItem, reason: FailureReason) => TFailure,
): {
  addSucceeded: TItem[];
  addFailed: TFailure[];
  removeSucceeded: TItem[];
  removeFailed: TFailure[];
} => {
  const add = partitionByErrors(
    addRequested,
    errors,
    ActionType.ADD,
    isItemInError,
    createFailure,
  );
  const remove = partitionByErrors(
    removeRequested,
    errors,
    ActionType.REMOVE,
    isItemInError,
    createFailure,
  );
  return {
    addSucceeded: add.succeeded,
    addFailed: add.failed,
    removeSucceeded: remove.succeeded,
    removeFailed: remove.failed,
  };
};

// Adds "We found" prefix if not already present
const addWeFoundPrefix = (msg: string, lowercase = false): string => {
  if (!msg) return '';
  if (/^[Ww]e /.test(msg)) {
    return lowercase ? msg.charAt(0).toLowerCase() + msg.slice(1) : msg;
  }
  return lowercase ? `we found ${msg}` : `We found ${msg}`;
};

// Groups failures by reason and formats error messages using templates
const buildFailureMessage = <TItem>(
  failures: Array<{ item: TItem; reason: FailureReason }>,
  getDisplayName: (item: TItem) => string | undefined,
  templates: Record<FailureReason, ErrorTemplate>,
  lowercase = false,
  formatNamesForError?: (names: string[]) => string[],
): string => {
  if (!failures.length) return '';

  // Collect all names with their reasons and assign sequential indices
  type NameWithReason = {
    name: string;
    reason: FailureReason;
    index: number;
  };
  const allNames: NameWithReason[] = [];

  // First pass: count non-NOT_FOUND items
  const includesOtherFailures = failures.some(
    ({ reason }) => reason !== FailureReason.NOT_FOUND,
  );

  // Assign indices: NOT_FOUND gets separate sequence when combined with others
  let nonNotFoundIndex = 1;
  let notFoundIndex = 1;
  for (const { item, reason } of failures) {
    const name = getDisplayName(item);
    if (name) {
      const isNotFound = reason === FailureReason.NOT_FOUND;
      // When NOT_FOUND is combined with other errors, use separate index sequence
      const index =
        isNotFound && includesOtherFailures
          ? notFoundIndex++
          : nonNotFoundIndex++;
      allNames.push({ name, reason, index });
    }
  }

  // Group by reason, preserving indices
  const grouped = new Map<
    FailureReason,
    Array<{ name: string; index: number }>
  >(Object.values(FailureReason).map((reason) => [reason, []] as const));

  for (const { name, reason, index: idx } of allNames) {
    grouped.get(reason)!.push({ name, index: idx });
  }

  // Format error messages for each failure reason
  const entries = Array.from(grouped.entries()).filter(
    ([, items]) => items.length > 0,
  );
  const includesNotFoundFailures =
    grouped.get(FailureReason.NOT_FOUND)!.length > 0;
  // Count non-NOT_FOUND error types for index decision
  const nonNotFoundTypeCount = entries.filter(
    ([reason]) => reason !== FailureReason.NOT_FOUND,
  ).length;

  const messages = entries.map(([reason, items]) => {
    const names = items.map((item) => item.name);
    const isNotFound = reason === FailureReason.NOT_FOUND;

    // Use indices if:
    // - Non-NOT_FOUND: multiple items OR multiple non-NOT_FOUND error types
    // - NOT_FOUND: only when it's the ONLY error type with 2+ items
    const shouldUseIndices =
      formatNamesForError &&
      (isNotFound
        ? entries.length === 1 && names.length > 1
        : names.length > 1 || nonNotFoundTypeCount >= 2);

    const formattedNames = shouldUseIndices
      ? items.map((item) => `(${item.index}) ${item.name}`)
      : names;

    const list = formatTruncatedList(
      formattedNames,
      LIST_TRUNCATE_LIMIT,
      formatListWithAnd,
    );
    const template = templates[reason];

    // NOT_FOUND uses lowercase when combined with other error types
    const hasMultipleErrorTypes = entries.length > 1;
    const useCapitalize = isNotFound
      ? !hasMultipleErrorTypes && !lowercase
      : !lowercase;

    return 'format' in template
      ? template.format(list, useCapitalize)
      : names.length > 1
        ? template.multiple(list)
        : template.single(list);
  });

  if (!messages.length) return '';
  if (messages.length === 1) return messages[0] || '';

  // NOT_FOUND must be at the end, joined with " and " (no comma)
  if (includesNotFoundFailures && messages.length >= 2) {
    // Find NOT_FOUND index (because it can't be the last in array)
    const notFoundIndex = entries.findIndex(
      ([reason]) => reason === FailureReason.NOT_FOUND,
    );
    // Ensure NOT_FOUND was found before processing
    if (notFoundIndex >= 0) {
      // Extract the NOT_FOUND message from its position
      const notFoundMsg = messages[notFoundIndex];
      // Filter out NOT_FOUND message to get all other error messages
      const otherMessages = messages.filter((_, idx) => idx !== notFoundIndex);

      // If only one other error type exists, join with " and " (no comma)
      if (otherMessages.length === 1) {
        return `${otherMessages[0]} and ${notFoundMsg}`;
      }
      // If multiple other error types exist, join them with commas, then add NOT_FOUND with " and "
      return `${otherMessages.join(', ')} and ${notFoundMsg}`;
    }
  }

  if (messages.length === 2) return `${messages[0]}, and ${messages[1]}`;
  return formatListWithAnd(messages);
};

// Checks if error parsing failed (remove-only operation with errors but no matched failures)
const checkParsingStatus = (
  addRequested: unknown[],
  errorMessages: (string | undefined)[],
  allFailures: unknown[],
): boolean => {
  const isRemoveOnly = !addRequested.length;
  return isRemoveOnly && errorMessages.some(Boolean) && !allFailures.length;
};

const checkOperationStatus = <TItem>(
  parsingFailed: boolean,
  validation: OperationValidation<TItem, unknown>,
  allFailures: unknown[],
): { hasSuccess: boolean; hasFailures: boolean } => {
  const hasSuccess =
    !parsingFailed &&
    (validation.addSucceeded.length > 0 ||
      validation.removeSucceeded.length > 0);
  const hasFailures = parsingFailed || allFailures.length > 0;

  return { hasSuccess, hasFailures };
};

// Calculates complete validation state from requested items and errors
const calculateValidationState = <TItem, TFailure>(
  addRequested: TItem[],
  removeRequested: TItem[],
  errorMessages: (string | undefined)[],
  parsedErrors: ParsedError[],
  config: UpdateProcessConfig<TItem, TFailure>,
): ValidationState<TItem, TFailure> => {
  const validation = validateOperations(
    addRequested,
    removeRequested,
    parsedErrors,
    config.isItemInError,
    config.createFailure,
  );

  const allFailures = [...validation.addFailed, ...validation.removeFailed];
  const parsingFailed = checkParsingStatus(
    addRequested,
    errorMessages,
    allFailures,
  );
  const { hasSuccess, hasFailures } = checkOperationStatus(
    parsingFailed,
    validation,
    allFailures,
  );

  return {
    validation,
    allFailures,
    parsingFailed,
    hasSuccess,
    hasFailures,
  };
};

const extractItemNamesFromFailures = <TItem, TFailure>(
  allFailures: TFailure[],
  config: UpdateProcessConfig<TItem, TFailure>,
): Array<{ item: TItem; reason: FailureReason }> => {
  return allFailures.map((f) => ({
    item: config.getItemFromFailure(f),
    reason: config.getReasonFromFailure(f),
  }));
};

// Builds structured error message from failures using templates
const buildStructuredErrorMessage = <TItem, TFailure>(
  allFailures: TFailure[],
  config: UpdateProcessConfig<TItem, TFailure>,
  lowercase = false,
): string => {
  const failureItems = extractItemNamesFromFailures(allFailures, config);
  const structuredMsg = buildFailureMessage<TItem>(
    failureItems,
    config.getDisplayName,
    config.errorTemplates,
    lowercase,
    config.formatNamesForError,
  );

  return structuredMsg ? addWeFoundPrefix(structuredMsg, lowercase) : '';
};

const extractNamesFromRequestedItems = <TItem>(
  removeRequested: TItem[],
  getNameFromItem: (item: TItem) => string | undefined,
  formatNamesForError?: (names: string[]) => string[],
): string[] => {
  if (!removeRequested.length) return [];

  const plainNames = removeRequested
    .map(getNameFromItem)
    .filter((s): s is string => !!s);
  // Format with indices only if there are 2+ items
  return plainNames.length > 1 && formatNamesForError
    ? formatNamesForError(plainNames)
    : plainNames;
};

// Formats "not found" error message for items that couldn't be removed
const formatNotFoundMessage = (names: string[], lowercase: boolean): string => {
  if (!names.length) return '';

  const list = formatTruncatedList(
    names,
    LIST_TRUNCATE_LIMIT,
    formatListWithAnd,
  );
  return `${lowercase ? "we couldn't" : "We couldn't"} find ${list} to remove`;
};

const buildParsingErrorMessage = <TItem, TFailure>(
  removeRequested: TItem[],
  errorMessages: (string | undefined)[],
  config: UpdateProcessConfig<TItem, TFailure>,
  lowercase = false,
): string => {
  const rawMsg = errorMessages.find(Boolean);
  const names = rawMsg
    ? config.extractNamesFromError(rawMsg)
    : extractNamesFromRequestedItems(
        removeRequested,
        config.getNameFromItem,
        config.formatNamesForError,
      );

  return formatNotFoundMessage(names, lowercase);
};

// Builds error message: structured from failures, or parsing fallback, or empty
const buildErrorMessage = <TItem, TFailure>(
  allFailures: TFailure[],
  removeRequested: TItem[],
  errorMessages: (string | undefined)[],
  parsingFailed: boolean,
  config: UpdateProcessConfig<TItem, TFailure>,
  lowercase = false,
): string => {
  const structuredMsg = buildStructuredErrorMessage(
    allFailures,
    config,
    lowercase,
  );
  if (structuredMsg) return structuredMsg;

  if (parsingFailed) {
    return buildParsingErrorMessage(
      removeRequested,
      errorMessages,
      config,
      lowercase,
    );
  }

  return '';
};

// Matches requested item with result items using type-specific matcher
const matchRequestedWithResultItems = <TItem>(
  requested: TItem,
  resultItems: TItem[],
  matchCardData: (requested: TItem, result: TItem) => boolean,
): TItem => {
  return resultItems.find((r) => matchCardData(requested, r)) || requested;
};

// Builds card data by matching requested items with result items (for display)
const buildCardData = <TItem>(
  addSucceeded: TItem[],
  resultItems: TItem[] | undefined,
  matchCardData: (requested: TItem, result: TItem) => boolean,
): TItem[] => {
  if (!resultItems?.length) {
    return addSucceeded;
  }

  return addSucceeded.map((req) =>
    matchRequestedWithResultItems(req, resultItems, matchCardData),
  );
};

const buildErrorResult = <TItem, TResult extends UpdateOperationResult<TItem>>(
  errorMsg: string,
  fallbackError: string,
): TResult => {
  const result: UpdateOperationResult<TItem> = {
    showCard: false,
    cardData: [],
    message: errorMsg || fallbackError,
    messageType: MessageType.ERROR,
  };
  return result as TResult;
};

// Builds success result when all operations succeeded
const buildSuccessResult = <
  TItem,
  TResult extends UpdateOperationResult<TItem>,
>(
  addSucceeded: TItem[],
  cardData: TItem[],
  successMsg: string,
  fallbackMessage: string | undefined,
): TResult => {
  const result: UpdateOperationResult<TItem> = {
    showCard: addSucceeded.length > 0,
    cardData,
    message: successMsg || fallbackMessage || '',
    messageType: MessageType.SUCCESS,
  };
  return result as TResult;
};

// Lowercases "We" for proper sentence structure after "However, "
const normalizePronounCasing = (msg: string): string => {
  if (!msg) return msg;
  return msg.replace(MESSAGE_PATTERN.LOWERCASE_WE, 'we ');
};

// Builds partial success result when some operations succeeded and some failed
const buildPartialSuccessResult = <
  TItem,
  TResult extends UpdateOperationResult<TItem>,
>(
  addSucceeded: TItem[],
  cardData: TItem[],
  successMsg: string,
  errorMsg: string,
  partialSuccessMessage: (successMsg: string, failureMsg: string) => string,
  fallbackError: string,
): TResult => {
  // Lowercase error message when used after "However, " for proper sentence structure
  const lowercasedErrorMsg = errorMsg ? normalizePronounCasing(errorMsg) : '';
  const result: UpdateOperationResult<TItem> = {
    showCard: addSucceeded.length > 0,
    cardData,
    message: errorMsg
      ? partialSuccessMessage(successMsg, lowercasedErrorMsg)
      : successMsg || fallbackError,
    messageType: MessageType.SUCCESS,
  };
  return result as TResult;
};

const buildUpdateResult = <
  TItem,
  TFailure,
  TResult extends UpdateOperationResult<TItem>,
>(
  state: ValidationState<TItem, TFailure>,
  cardData: TItem[],
  errorMsg: string,
  successMsg: string,
  successMessage: string | undefined,
  config: UpdateProcessConfig<TItem, TFailure>,
): TResult => {
  if (!state.hasSuccess) {
    return buildErrorResult<TItem, TResult>(errorMsg, config.errorMessage);
  }

  if (!state.hasFailures) {
    return buildSuccessResult<TItem, TResult>(
      state.validation.addSucceeded,
      cardData,
      successMsg,
      successMessage,
    );
  }

  return buildPartialSuccessResult<TItem, TResult>(
    state.validation.addSucceeded,
    cardData,
    successMsg,
    errorMsg,
    config.partialSuccessMessage,
    config.errorMessage,
  );
};

// Prepares error data: collects messages and parses them
const prepareErrorData = (
  duplicatesMessage?: string,
  invalidMessage?: string,
  invalidDatesMessage?: string,
  invalidUrlsMessage?: string,
  nonExistingMessage?: string,
): { errorMessages: (string | undefined)[]; parsedErrors: ParsedError[] } => {
  const errorMessages = [
    duplicatesMessage,
    invalidMessage,
    invalidDatesMessage,
    invalidUrlsMessage,
    nonExistingMessage,
  ];
  const parsedErrors = errorMessages
    .map((m) => matchFailureReason(m, FAILURE_CONFIGS))
    .filter((e): e is ParsedError => e !== null);
  return { errorMessages, parsedErrors };
};

// Prepares validation and card data for result building
const prepareValidationData = <TItem, TFailure>(
  addRequested: TItem[],
  removeRequested: TItem[],
  errorMessages: (string | undefined)[],
  parsedErrors: ParsedError[],
  resultItems: TItem[] | undefined,
  config: UpdateProcessConfig<TItem, TFailure>,
): {
  state: ValidationState<TItem, TFailure>;
  cardData: TItem[];
} => {
  const state = calculateValidationState(
    addRequested,
    removeRequested,
    errorMessages,
    parsedErrors,
    config,
  );

  const cardData = buildCardData(
    state.validation.addSucceeded,
    resultItems,
    config.matchCardData,
  );

  return { state, cardData };
};

// Builds success and error messages from validation state
const buildOperationMessages = <TItem, TFailure>(
  state: ValidationState<TItem, TFailure>,
  removeRequested: TItem[],
  errorMessages: (string | undefined)[],
  config: UpdateProcessConfig<TItem, TFailure>,
): { successMsg: string; errorMsg: string } => {
  const successMsg = config.buildSuccessMessage(
    state.validation.addSucceeded,
    state.validation.removeSucceeded,
  );

  const errorMsg = buildErrorMessage(
    state.allFailures,
    removeRequested,
    errorMessages,
    state.parsingFailed,
    config,
  );

  return { successMsg, errorMsg };
};

// Main generic function to process update operations (education/certification)
// Orchestrates validation, error handling, and result building
const processUpdate = <
  TItem,
  TFailure,
  TResult extends UpdateOperationResult<TItem>,
>(
  addRequested: TItem[],
  removeRequested: TItem[],
  config: UpdateProcessConfig<TItem, TFailure>,
  options?: {
    duplicatesMessage?: string;
    invalidMessage?: string;
    invalidDatesMessage?: string;
    invalidUrlsMessage?: string;
    nonExistingMessage?: string;
    resultItems?: TItem[];
    successMessage?: string;
  },
): TResult => {
  const {
    duplicatesMessage,
    invalidMessage,
    invalidDatesMessage,
    invalidUrlsMessage,
    nonExistingMessage,
    resultItems,
    successMessage,
  } = options || {};

  // Step 1: Prepare error data (messages and parsed errors)
  const { errorMessages, parsedErrors } = prepareErrorData(
    duplicatesMessage,
    invalidMessage,
    invalidDatesMessage,
    invalidUrlsMessage,
    nonExistingMessage,
  );

  // Step 2: Prepare validation state and card data
  const { state, cardData } = prepareValidationData(
    addRequested,
    removeRequested,
    errorMessages,
    parsedErrors,
    resultItems,
    config,
  );

  // Step 3: Build success and error messages
  const { successMsg, errorMsg } = buildOperationMessages(
    state,
    removeRequested,
    errorMessages,
    config,
  );

  // Step 4: Build and return final result
  return buildUpdateResult(
    state,
    cardData,
    errorMsg,
    successMsg,
    successMessage,
    config,
  );
};

/**
 * Routes update items into the correct add/remove partitions for error processing.
 * Items that match any REMOVE-partition error (e.g. "unmatched", "non-existing")
 * are moved to the remove list; the rest join the add list.
 */
const partitionUpdateItems = <TItem>(
  addItems: TItem[],
  updateItems: TItem[],
  removeItems: TItem[],
  isItemInError: (item: TItem, errorText: string) => boolean,
  removePartitionMessages: (string | undefined)[],
): { effectiveAdd: TItem[]; effectiveRemove: TItem[] } => {
  const activeMessages = removePartitionMessages.filter((m): m is string =>
    Boolean(m),
  );
  const failedUpdates = updateItems.filter((item) =>
    activeMessages.some((msg) => isItemInError(item, msg)),
  );

  return {
    effectiveAdd: [
      ...addItems,
      ...updateItems.filter((item) => !failedUpdates.includes(item)),
    ],
    effectiveRemove: [...removeItems, ...failedUpdates],
  };
};

// Returns an error result when all add items lack required fields and the server reports an error
// Returns null to continue with normal processing
const buildMissingFieldsErrorResult = <
  TItem,
  TResult extends UpdateOperationResult<TItem>,
>(
  effectiveAdd: TItem[],
  validAddItems: TItem[],
  invalidMessage: string | undefined,
  buildNames: (items: TItem[]) => string[],
  fallbackError: string,
): TResult | null => {
  if (validAddItems.length || !effectiveAdd.length || !invalidMessage)
    return null;

  const names = buildNames(effectiveAdd);
  const list = formatTruncatedList(
    names,
    LIST_TRUNCATE_LIMIT,
    formatListWithAnd,
  );

  return buildErrorResult<TItem, TResult>(
    list ? `We couldn't find ${list} to update/remove` : '',
    fallbackError,
  );
};

export const processEducationUpdate = (options?: {
  addEducations?: EmployeeEducation[];
  updateEducations?: EmployeeEducation[];
  removeEducations?: EmployeeEducation[];
  duplicatesMessage?: string;
  invalidMessage?: string;
  invalidDatesMessage?: string;
  nonExistingMessage?: string;
  resultEducations?: EmployeeEducation[];
  successMessage?: string;
}): EducationUpdateResult => {
  const {
    addEducations = [],
    updateEducations = [],
    removeEducations = [],
    duplicatesMessage,
    invalidMessage,
    invalidDatesMessage,
    nonExistingMessage,
    resultEducations,
    successMessage,
  } = options ?? {};

  const { effectiveAdd, effectiveRemove } = partitionUpdateItems(
    addEducations,
    updateEducations,
    removeEducations,
    isEducationInError,
    [invalidMessage, nonExistingMessage],
  );

  // Filter only when the server reports an error since items with missing required fields
  // cannot be matched to error text and would silently appear as succeeded
  const validAddItems = invalidMessage
    ? effectiveAdd.filter(
        (e) => !!e.school?.trim() && !!e.degree?.trim() && !!e.major?.trim(),
      )
    : effectiveAdd;

  const missingFieldsResult = buildMissingFieldsErrorResult<
    EmployeeEducation,
    EducationUpdateResult
  >(
    effectiveAdd,
    validAddItems,
    invalidMessage,
    (items) =>
      items
        .map((e) => [e.degree, e.major, e.school].filter(Boolean).join(' in '))
        .filter(Boolean),
    CHATBOT_MESSAGES.EDUCATION_UPDATE_ERROR,
  );
  if (missingFieldsResult) return missingFieldsResult;

  return processUpdate<
    EmployeeEducation,
    EducationFailure,
    EducationUpdateResult
  >(
    validAddItems,
    effectiveRemove,
    {
      isItemInError: isEducationInError,
      createFailure: (education, reason) => ({ education, reason }),
      getItemFromFailure: (failure) => failure.education,
      getReasonFromFailure: (failure) => failure.reason,
      getDisplayName: (e) => e.school,
      errorTemplates: EDUCATION_ERROR_TEMPLATES,
      buildSuccessMessage: buildEducationSuccessMessage,
      extractNamesFromError: extractSchoolNames,
      getNameFromItem: (e) => e.school,
      matchCardData: matchEducationByKeyFields,
      errorMessage: CHATBOT_MESSAGES.EDUCATION_UPDATE_ERROR,
      partialSuccessMessage: CHATBOT_MESSAGES.EDUCATION_UPDATE_PARTIAL_SUCCESS,
    },
    {
      duplicatesMessage,
      invalidMessage,
      invalidDatesMessage,
      nonExistingMessage,
      resultItems: resultEducations,
      successMessage,
    },
  );
};

export const processCertificationUpdate = (options?: {
  addCerts?: EmployeeCertification[];
  updateCerts?: EmployeeCertification[];
  removeCerts?: EmployeeCertification[];
  duplicatesMessage?: string;
  invalidMessage?: string;
  invalidDatesMessage?: string;
  invalidUrlsMessage?: string;
  nonExistingMessage?: string;
  resultCertifications?: EmployeeCertification[];
  successMessage?: string;
}): CertificationUpdateResult => {
  const {
    addCerts = [],
    updateCerts = [],
    removeCerts = [],
    duplicatesMessage,
    invalidMessage,
    invalidDatesMessage,
    invalidUrlsMessage,
    nonExistingMessage,
    resultCertifications,
    successMessage,
  } = options ?? {};

  const { effectiveAdd, effectiveRemove } = partitionUpdateItems(
    addCerts,
    updateCerts,
    removeCerts,
    isCertificationInError,
    [invalidMessage, nonExistingMessage],
  );

  // Filter only when the server reports an error since items with missing required fields
  // cannot be matched to error text and would silently appear as succeeded
  const validAddItems = invalidMessage
    ? effectiveAdd.filter((c) => !!c.name?.trim())
    : effectiveAdd;

  const missingFieldsResult = buildMissingFieldsErrorResult<
    EmployeeCertification,
    CertificationUpdateResult
  >(
    effectiveAdd,
    validAddItems,
    invalidMessage,
    (items) => items.map((c) => c.name).filter(Boolean) as string[],
    CHATBOT_MESSAGES.CERTIFICATION_UPDATE_ERROR,
  );
  if (missingFieldsResult) return missingFieldsResult;

  // Format names with indices only if there are 2+ names
  const formatNamesWithIndices = (names: string[]): string[] =>
    names.length > 1 ? names.map((name, idx) => `(${idx + 1}) ${name}`) : names;

  return processUpdate<
    EmployeeCertification,
    CertificationFailure,
    CertificationUpdateResult
  >(
    validAddItems,
    effectiveRemove,
    {
      isItemInError: isCertificationInError,
      createFailure: (certification, reason) => ({ certification, reason }),
      getItemFromFailure: (failure) => failure.certification,
      getReasonFromFailure: (failure) => failure.reason,
      getDisplayName: (c) => c.name,
      errorTemplates: CERTIFICATION_ERROR_TEMPLATES,
      buildSuccessMessage: buildCertificationSuccessMessage,
      extractNamesFromError: (rawMsg: string) =>
        formatNamesWithIndices(extractCertificationNames(rawMsg)),
      getNameFromItem: (c) => c.name,
      formatNamesForError: formatNamesWithIndices,
      matchCardData: (req, r) =>
        normalizeString(r.name || '') === normalizeString(req.name || ''),
      errorMessage: CHATBOT_MESSAGES.CERTIFICATION_UPDATE_ERROR,
      partialSuccessMessage:
        CHATBOT_MESSAGES.CERTIFICATION_UPDATE_PARTIAL_SUCCESS,
    },
    {
      duplicatesMessage,
      invalidMessage,
      invalidDatesMessage,
      invalidUrlsMessage,
      nonExistingMessage,
      resultItems: resultCertifications,
      successMessage,
    },
  );
};

const loadFromStorage = (): Record<string, string> =>
  getLocalStorage<Record<string, string>>(
    LOCAL_STORAGE_KEYS.CHATBOT_MESSAGE_IMAGES,
    {},
  ) ?? {};

const saveToStorage = (data: Record<string, string>): void => {
  setLocalStorage(LOCAL_STORAGE_KEYS.CHATBOT_MESSAGE_IMAGES, data);
};

export const setMessageImage = (messageId: string, base64: string): void => {
  const data = loadFromStorage();
  data[messageId] = base64;
  saveToStorage(data);
};

export const getMessageImage = (
  messageId: string | undefined,
): string | undefined => {
  if (!messageId) return undefined;
  return loadFromStorage()[messageId];
};

export const clearAllMessageImages = (): void => {
  removeLocalStorage(LOCAL_STORAGE_KEYS.CHATBOT_MESSAGE_IMAGES);
};

/**
 * Returns true when a run is in progress and the last message is an incomplete
 * assistant text message (i.e. the stop/cancel button should be shown).
 */
export const isAssistantGenerating = (
  inProgress: boolean,
  lastMessage: Message | undefined,
): boolean =>
  inProgress &&
  !!lastMessage?.isTextMessage?.() &&
  lastMessage.role === MessageRole.Assistant &&
  !('isComplete' in lastMessage && lastMessage.isComplete);

interface FinishingLabelMessage {
  role?: string;
  content?: unknown;
  toolCalls?: Array<{ type?: string; function?: { name: string } }>;
}

interface FinishingLabelVisibilityOptions {
  hasCompletedToolSubComponent: boolean;
  isCurrentMessage: boolean;
  isProcessing: boolean;
  isWaitingForUserResponse?: boolean;
  isUnresponsiveSystem?: boolean;
}

interface GenerativeUISubComponentProps {
  RenderComponent?: unknown;
  toolCall?: { function?: { name?: string } };
  toolMessage?: unknown;
  isExecuting?: boolean;
}

export const getGenerativeUISubComponentProps = (
  subComponent: React.ReactNode,
): GenerativeUISubComponentProps | null => {
  if (!React.isValidElement(subComponent)) return null;

  return subComponent.props as GenerativeUISubComponentProps;
};

export const getHasCompletedToolSubComponent = (
  subComponent: React.ReactNode,
): boolean => {
  if (Array.isArray(subComponent)) {
    return subComponent.some(getHasCompletedToolSubComponent);
  }

  const props = getGenerativeUISubComponentProps(subComponent);
  const isToolSubComponent = !!props?.RenderComponent || !!props?.toolCall;

  return isToolSubComponent && props?.isExecuting !== true;
};

const isVisibleMessage = (message: unknown) => {
  const role = (message as { role?: string } | null | undefined)?.role;

  return role === Role.User || role === Role.Assistant;
};

export const getIsLatestVisibleMessage = (
  isCurrentMessage: boolean | undefined,
  messages: unknown[] | undefined,
  message: unknown,
) => {
  if (!messages?.length) {
    return typeof isCurrentMessage === 'boolean' ? isCurrentMessage : true;
  }

  const messageIndex = messages.findIndex((item) => item === message);
  if (messageIndex === -1) {
    return typeof isCurrentMessage === 'boolean' ? isCurrentMessage : true;
  }

  for (let index = messages.length - 1; index > messageIndex; index -= 1) {
    if (isVisibleMessage(messages[index])) return false;
  }

  return true;
};

export const getHasCompletedToolSubComponentInCurrentUserRequest = (
  messages: unknown[] | undefined,
  message: unknown,
) => {
  if (!messages?.length) return false;

  const currentMessageIndex = messages.findIndex((item) => item === message);
  if (currentMessageIndex === -1) return false;

  for (let index = currentMessageIndex - 1; index >= 0; index -= 1) {
    const currentMessage = messages[index] as
      | { role?: string; generativeUI?: () => React.ReactNode }
      | null
      | undefined;

    if (currentMessage?.role === Role.User) break;
    if (currentMessage?.role !== Role.Assistant) continue;

    if (getHasCompletedToolSubComponent(currentMessage.generativeUI?.())) {
      return true;
    }
  }

  return false;
};

/**
 * Names of the tools registered through the human-in-the-loop pattern
 */
const HUMAN_IN_THE_LOOP_TOOL_NAMES: ReadonlySet<string> = new Set([
  COPILOT_TOOLS.RICH_TEXT_DESCRIPTION_EDITOR.NAME,
  COPILOT_TOOLS.REMOVE_MEMBER_PROJECT.NAME,
  COPILOT_TOOLS.ARCHIVE_PROJECT_CONFIRM.NAME,
  COPILOT_TOOLS.DEACTIVATE_EMPLOYEE_CONFIRM.NAME,
]);

/**
 * Checks if a generative UI sub component is a human-in-the-loop tool render
 * that the user has already interacted with (i.e. it is no longer executing and
 * has produced a tool result message).
 */
export const getHasCompletedHumanInTheLoopTool = (
  subComponent: React.ReactNode,
): boolean => {
  if (Array.isArray(subComponent)) {
    return subComponent.some(getHasCompletedHumanInTheLoopTool);
  }

  const props = getGenerativeUISubComponentProps(subComponent);
  if (!props || props.isExecuting === true || !props.toolMessage) return false;

  const toolName = props.toolCall?.function?.name;

  return !!toolName && HUMAN_IN_THE_LOOP_TOOL_NAMES.has(toolName);
};

/**
 * Determines whether the given assistant message is a redundant trailing summary
 * that the agent appended right after the user interacted with a human-in-the-loop
 * tool (e.g. "The event description has been updated successfully...").
 */
export const getIsRedundantMessageAfterHumanInTheLoop = (
  messages: unknown[] | undefined,
  message: unknown,
): boolean => {
  if (!messages?.length) return false;

  const currentMessageIndex = messages.findIndex((item) => item === message);
  if (currentMessageIndex === -1) return false;

  for (let index = currentMessageIndex - 1; index >= 0; index -= 1) {
    const previousMessage = messages[index] as
      | { role?: string; generativeUI?: () => React.ReactNode }
      | null
      | undefined;

    if (previousMessage?.role === Role.User) break;
    if (previousMessage?.role !== Role.Assistant) continue;

    if (getHasCompletedHumanInTheLoopTool(previousMessage.generativeUI?.())) {
      return true;
    }
  }

  return false;
};

export const getShouldRenderFinishingLabel = (
  optionsOrIsProcessing: boolean | FinishingLabelVisibilityOptions,
  message?: FinishingLabelMessage | null | undefined,
): boolean => {
  if (typeof optionsOrIsProcessing !== 'boolean') {
    return (
      optionsOrIsProcessing.isProcessing &&
      optionsOrIsProcessing.hasCompletedToolSubComponent &&
      optionsOrIsProcessing.isCurrentMessage &&
      !optionsOrIsProcessing.isWaitingForUserResponse &&
      !optionsOrIsProcessing.isUnresponsiveSystem
    );
  }

  const isProcessing = optionsOrIsProcessing;
  if (!isProcessing || !message) return false;
  return (
    ((message.role as string) === Role.Tool &&
      (message.content as { success?: string })?.success === 'true') ||
    (message.role === Role.Assistant &&
      ((Array.isArray(message.toolCalls) &&
        message.toolCalls.some(
          (call) =>
            call?.type === 'function' &&
            call?.function?.name === 'updateWorkingMemory',
        )) ||
        (!('name' in message) && !('toolCalls' in message))))
  );
};
